import {
  AccountingPackageModal as AccountingPackageModalComponent,
  AnnualReportLateWarningModal as AnnualReportLateWarningModalComponent,
  PurchaseCard,
} from '@components'

import { Link, useModal, withModalConditionalRender } from '@billy/design-system'
import React, { ReactElement, useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ModalId } from '../../../../enums/modalId'
import { useAnnualReportPurchasing } from '../../contexts/annualReportPurchasingContext'
import { AnnualReportType } from '../../enums/annualReportType'
import { getARTypeFeatures } from '../../utils/getARTypeFeatures'

const AccountingPackageModal = withModalConditionalRender(AccountingPackageModalComponent)
const AnnualReportLateWarningModal = withModalConditionalRender(AnnualReportLateWarningModalComponent)

export const AccountingPackageCard = (): ReactElement => {
  const { t } = useTranslation()
  const { accountingPackagePrice, handlePurchaseAccountingPackage, isAccountingPackageSoldOut, year } =
    useAnnualReportPurchasing()
  const { open: openAccountingPackageModal } = useModal(ModalId.AccountingPackageModal)
  const { open: openLateWarningModal } = useModal(ModalId.AnnualReportLateWarningModalAccountingPackage)

  const accountingPackageHighlight = useMemo(
    (): string | undefined =>
      isAccountingPackageSoldOut ? t('annual_report.package.we_do_it.sold_out', { year }) : undefined,
    [isAccountingPackageSoldOut, t, year],
  )

  const handleBuyButtonClick = useCallback(() => {
    openLateWarningModal()
  }, [openLateWarningModal])

  const handleWarningConfirm = useCallback(() => {
    openAccountingPackageModal()
  }, [openAccountingPackageModal])

  return (
    <PurchaseCard highlightedText={accountingPackageHighlight}>
      <PurchaseCard.Header
        description={
          <Trans i18nKey="annual_report.package.accounting_package.description">
            Get everything done by our accountant
            <br />
            partner*
          </Trans>
        }
        title={t('annual_report.package.accounting_package.title')}
      />
      <PurchaseCard.Button onClick={handleBuyButtonClick} disabled={isAccountingPackageSoldOut}>
        {t('annual_report.package.buy')}
      </PurchaseCard.Button>
      <PurchaseCard.PriceSection price={accountingPackagePrice} priceSuffix={t('annual_report.package.price_suffix')} />
      <PurchaseCard.Features
        footer={
          <Trans i18nKey="annual_report.package.accounting_package.footer">
            *Our accountant partner
            <Link href="https://aarsregnskab.dk/" underlined withAnimation>
              Årsregnskabet.dk
            </Link>
            will do the work
          </Trans>
        }
        header={t('annual_report.package.included')}
        items={getARTypeFeatures(AnnualReportType.AccountingPackage)}
      />
      <AnnualReportLateWarningModal
        id={ModalId.AnnualReportLateWarningModalAccountingPackage}
        onConfirm={handleWarningConfirm}
      />
      <AccountingPackageModal
        id={ModalId.AccountingPackageModal}
        onAcceptButtonClick={handlePurchaseAccountingPackage}
      />
    </PurchaseCard>
  )
}
